class HeroBannerTabsSlider extends HTMLElement {
  constructor() {
    super();
    this.defaultSpeed = 600;
    this.section = null;
    this._navBound = false;

    this.onNavClick = this.onNavClick.bind(this);
    this.onNavHover = this.onNavHover.bind(this);
    this.onNavKeydown = this.onNavKeydown.bind(this);

    window.addEventListener('shopify:block:select', e => {
      const selectedSlideIndex = +e.target.dataset.index;
      if (!Number.isNaN(selectedSlideIndex) && this.slider) {
        this.slider.slideTo(selectedSlideIndex, 600);
      }
    });
  }

  connectedCallback() {
    if (this.slider) return;

    this.section = this.closest('.hero-banner-tabs');
    this.mountSlider();
    this.initTabNavigation();
  }

  mountSlider() {
    const autoplayEnabled = this.dataset.autoplay === 'true';
    const autoplayStartDelay = Number(this.dataset.autoplayStartDelay) || 600;
    const autoplayOptions = {
      delay: Number(this.dataset.autoplayInterval) || 5000,
      disableOnInteraction: false
    };

    this.slider = new Swiper(this, {
      effect: 'fade',
      rewind: true,
      slidesPerView: 1,
      speed: 0,
      touchEventsTarget: 'container',
      allowTouchMove: true,
      followFinger: true,
      breakpoints: {
        750: {
          allowTouchMove: false,
          followFinger: false
        }
      },
      autoplay: autoplayEnabled ? autoplayOptions : false,
      on: {
        init: swiper => {
          this.handleSlideChange(swiper);
          this.updateTabState(swiper.activeIndex);
          this.prepareInitialRender(swiper, autoplayEnabled, autoplayStartDelay);
        },
        slideChange: swiper => {
          this.handleSlideChange(swiper);
          this.updateTabState(swiper.activeIndex);
        }
      }
    });
  }

  initTabNavigation() {
    if (!this.section || this._navBound) return;
    this._navBound = true;

    this.section.addEventListener('click', this.onNavClick);
    this.section.addEventListener('keydown', this.onNavKeydown);

    this.section.querySelectorAll('.hero-tabs-nav__item').forEach(item => {
      item.addEventListener('mouseenter', this.onNavHover);
    });
  }

  onSoundToggleClick(event) {
    const toggle = event.target.closest('[data-hero-sound-toggle]');
    if (!toggle) return false;

    event.preventDefault();
    event.stopPropagation();

    const slide = this.getActiveSlide();
    if (!slide) return true;

    const videos = slide.querySelectorAll('video');
    if (!videos.length) return true;

    const shouldUnmute = videos[0].muted;
    videos.forEach(video => {
      video.muted = !shouldUnmute;
    });

    this.updateSoundToggleState(toggle, shouldUnmute);
    return true;
  }

  getActiveSlide() {
    if (this.slider?.slides?.length) {
      return this.slider.slides[this.slider.activeIndex];
    }

    return this.querySelector('.hero__inner');
  }

  updateSoundToggleState(toggle, isUnmuted) {
    const iconOn = toggle.querySelector('.hero-tabs__sound-icon--on');
    const iconOff = toggle.querySelector('.hero-tabs__sound-icon--off');

    if (iconOn) iconOn.hidden = !isUnmuted;
    if (iconOff) iconOff.hidden = isUnmuted;
    toggle.setAttribute('aria-label', isUnmuted ? 'Mute video' : 'Unmute video');
  }

  onNavClick(event) {
    if (this.onSoundToggleClick(event)) return;

    const paginationDot = event.target.closest('[data-hero-pagination-index]');
    if (paginationDot) {
      event.preventDefault();
      event.stopPropagation();

      const index = Number(paginationDot.dataset.heroPaginationIndex);
      if (!Number.isNaN(index)) {
        this.goToSlide(index);
      }
      return;
    }

    const slideIndex = this.getSlideIndexFromTarget(event.target);
    if (slideIndex === null) return;

    if (event.target.closest('a')) return;

    event.preventDefault();
    this.goToSlide(slideIndex);
  }

  onNavHover(event) {
    const item = event.currentTarget;
    const slideIndex = Number(item.dataset.heroTabIndex);
    if (Number.isNaN(slideIndex)) return;

    this.goToSlide(slideIndex);
  }

  onNavKeydown(event) {
    const tabItem = event.target.closest('.hero-tabs-nav__item, .hero-tabs-pagination__dot');
    if (!tabItem) return;

    if (event.key !== 'Enter' && event.key !== ' ') return;

    event.preventDefault();

    const slideIndex = this.getSlideIndexFromTarget(tabItem);
    if (slideIndex !== null) {
      this.goToSlide(slideIndex);
    }
  }

  getSlideIndexFromTarget(target) {
    const tabItem = target.closest('[data-hero-tab-index]');
    if (tabItem) {
      const index = Number(tabItem.dataset.heroTabIndex);
      return Number.isNaN(index) ? null : index;
    }

    const paginationDot = target.closest('[data-hero-pagination-index]');
    if (paginationDot) {
      const index = Number(paginationDot.dataset.heroPaginationIndex);
      return Number.isNaN(index) ? null : index;
    }

    return null;
  }

  goToSlide(index) {
    if (!this.slider || Number.isNaN(index)) return;
    if (this.slider.activeIndex === index) return;
    this.slider.slideTo(index, this.defaultSpeed);
  }

  updateTabState(activeIndex) {
    if (!this.section) return;

    this.section.querySelectorAll('[data-hero-tab-index]').forEach(item => {
      const isActive = Number(item.dataset.heroTabIndex) === activeIndex;
      item.classList.toggle('is-active', isActive);
      item.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });

    this.section.querySelectorAll('[data-hero-mobile-panel]').forEach(panel => {
      const isActive = Number(panel.dataset.heroMobilePanel) === activeIndex;
      panel.classList.toggle('is-active', isActive);
      panel.hidden = !isActive;
    });

    this.section.querySelectorAll('[data-hero-pagination-index]').forEach(dot => {
      const isActive = Number(dot.dataset.heroPaginationIndex) === activeIndex;
      dot.classList.toggle('is-active', isActive);
      dot.setAttribute('aria-current', isActive ? 'true' : 'false');
    });

    this.section.querySelectorAll('[data-hero-sound-toggle]').forEach(toggle => {
      const index = Number(toggle.dataset.heroSoundIndex);
      const isActive = index === activeIndex;
      toggle.classList.toggle('is-hidden', !isActive);

      if (isActive) {
        const slide = this.getActiveSlide();
        const video = slide?.querySelector('video');
        this.updateSoundToggleState(toggle, video ? !video.muted : false);
      }
    });
  }

  prepareInitialRender(swiper, autoplayEnabled, autoplayStartDelay) {
    if (!swiper) return;

    if (autoplayEnabled && swiper.autoplay) {
      swiper.autoplay.stop();
    }

    this.waitForFirstSlideMedia(swiper).finally(() => {
      swiper.params.speed = this.defaultSpeed;
      this.classList.add('hero-slider--ready');

      if (autoplayEnabled && swiper.autoplay) {
        window.setTimeout(() => {
          swiper.autoplay.start();
        }, autoplayStartDelay);
      }
    });
  }

  waitForFirstSlideMedia(swiper) {
    return new Promise(resolve => {
      const activeSlide =
        swiper.slides && swiper.slides.length > 0
          ? swiper.slides[swiper.activeIndex]
          : this.querySelector('.hero__inner');

      if (!activeSlide) {
        resolve();
        return;
      }

      const mediaContainers = Array.from(activeSlide.querySelectorAll('.hero__media'));
      const visibleMedia =
        mediaContainers.find(media => window.getComputedStyle(media).display !== 'none') ||
        mediaContainers[0];

      if (!visibleMedia) {
        resolve();
        return;
      }

      const image = visibleMedia.querySelector('img');
      if (image) {
        if (image.complete && image.naturalWidth > 0) {
          resolve();
          return;
        }

        const onDone = () => resolve();
        image.addEventListener('load', onDone, { once: true });
        image.addEventListener('error', onDone, { once: true });
        return;
      }

      const video = visibleMedia.querySelector('video');
      if (video) {
        if (video.readyState >= 2) {
          resolve();
          return;
        }

        const onDone = () => resolve();
        video.addEventListener('loadeddata', onDone, { once: true });
        video.addEventListener('error', onDone, { once: true });
        return;
      }

      resolve();
    });
  }

  handleSlideChange(swiper) {
    const headerInner = document.querySelector('.header__inner');
    const heroInners = this.querySelectorAll('.hero__inner');

    if (headerInner && heroInners.length) {
      const activeInner = heroInners[swiper.activeIndex];
      if (activeInner) {
        document.documentElement.style.setProperty(
          '--transparent-header-menu-text-color',
          activeInner.dataset.headerMenuTextColor
        );
      }
    }

    this.syncSlideVideos(swiper);
  }

  syncSlideVideos(swiper) {
    const slides = swiper.slides && swiper.slides.length ? swiper.slides : [this.querySelector('.hero__inner')].filter(Boolean);

    slides.forEach((slide, index) => {
      if (!slide) return;

      slide.querySelectorAll('video').forEach(video => {
        if (index === swiper.activeIndex) {
          if (video.paused) {
            video.play().catch(() => {});
          }
        } else {
          video.pause();
          video.currentTime = 0;
          video.muted = true;
        }
      });
    });

    const activeToggle = this.section?.querySelector(
      `[data-hero-sound-toggle][data-hero-sound-index="${swiper.activeIndex}"]`
    );
    if (activeToggle) {
      const activeSlide = swiper.slides[swiper.activeIndex];
      const video = activeSlide?.querySelector('video');
      this.updateSoundToggleState(activeToggle, video ? !video.muted : false);
    }
  }

  disconnectedCallback() {
    if (!this.section || !this._navBound) return;

    this.section.removeEventListener('click', this.onNavClick);
    this.section.removeEventListener('keydown', this.onNavKeydown);

    this._navBound = false;
  }
}

customElements.define('hero-banner-tabs-slider', HeroBannerTabsSlider);
